import { Row, Col, Button, Icon, Tabs, Form, Spin } from 'antd';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { FormComponentProps } from 'antd/lib/form/Form';
import { fetchMyCustomers } from '../../../../core-module/redux/Customer/actions';
import { TitledContainer } from '../../../../components/TitledContainer/TitledContainer';
import { AppState } from '../../../../core-module/redux/reducers';
import { ProfileTab } from './Profile/ProfileTab';
import { hasRole, RoleTypes } from '../../../../core-module/helpers/RoleHelper';
import { OrderTab } from './Orders/OrderTab';

const { TabPane } = Tabs;

interface RouteParams {
  id: string;
}

interface OwnProps extends FormComponentProps, RouteComponentProps<RouteParams> {
  dispatch: Dispatch;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface State {
  customer: any;
  showAbsenceForm: boolean;
  absenceData: any;
  activeTab: string;
}

class CustomerById extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      customer: null,
      showAbsenceForm: false,
      absenceData: [],
      activeTab: this.getTabFromQuery(props.location.search)
    };
  }

  componentDidMount() {
    document.title = 'Logistics | Klant';

    const customer = this.findCustomerFromProps();
    if (customer) {
      this.setState({ customer });
      return;
    }

    this.props.dispatch(fetchMyCustomers(this.getRouteFilter()));
  }

  componentDidUpdate(prevProps: Props) {
    const currentId = this.props.match.params.id;
    const prevId = prevProps.match.params.id;
    const tabFromQuery = this.getTabFromQuery(this.props.location.search);

    if (
      this.props.location.search !== prevProps.location.search &&
      tabFromQuery !== this.state.activeTab
    ) {
      this.setState({ activeTab: tabFromQuery });
    }

    if (currentId !== prevId) {
      const customer = this.findCustomerFromProps();
      this.setState({
        customer: customer || null,
        activeTab: tabFromQuery,
        showAbsenceForm: false
      });
      if (!customer) {
        this.props.dispatch(fetchMyCustomers(this.getRouteFilter()));
      }
      return;
    }

    if (
      !this.state.customer &&
      prevProps.customer.myCustomers !== this.props.customer.myCustomers
    ) {
      const customer = this.findCustomerFromProps();
      if (customer) {
        this.setState({ customer });
      }
    }
  }

  getTabFromQuery = (search: string): string => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');

    if (tab === '1' || tab === '2' || tab === '3' || tab === '4') {
      return tab;
    }

    return '1';
  };

  setTabInQuery = (tabIndex: string) => {
    const params = new URLSearchParams(this.props.location.search);
    if (params.get('tab') === tabIndex) {
      return;
    }

    params.set('tab', tabIndex);
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: `?${params.toString()}`
    });
  };

  getRouteFilter = () => {
    const driver = this.props.auth.user.driver;
    const start: string = moment().subtract(1, 'month').format('YYYY-MM-DD');
    const end: string = moment().add(2, 'days').format('YYYY-MM-DD');
    let isDepotAdmin =
      hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN) ||
      hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN);

    const routeFilter: any = {
      filter: {
        include: [
          {
            relation: 'orders',
            scope: {
              fields: ['id', 'customer_id', 'postal_code', 'door_number', 'street_name', 'city'],
              include: [
                {
                  relation: 'customer',
                  scope: {
                    fields: [
                      'first_name',
                      'last_name',
                      'prefix',
                      'customer_id',
                      'camasys_id',
                      'postal_code',
                      'door_number',
                      'street_name',
                      'city'
                    ]
                  }
                }
              ]
            }
          }
        ],
        where: {
          and: [{ actual_date_driven: { between: [start, end] } }]
        },
        fields: ['id', 'date_driven', 'actual_date_driven', 'driver_id'],
        order: 'actual_date_driven DESC'
      }
    };

    isDepotAdmin
      ? routeFilter.filter.where.and.push({ depot_id: driver!.depot_id })
      : routeFilter.filter.where.and.push({ driver_id: driver!.id });

    return routeFilter;
  };

  findCustomerFromProps = () => {
    const customerId = Number(this.props.match.params.id);
    const locationState = this.props.location.state as any;

    if (locationState && locationState.customer && locationState.customer.id === customerId) {
      return locationState.customer;
    }

    return (this.props.customer.myCustomers as any[]).find(
      (customer: any) => customer.id === customerId
    );
  };

  onTabChange = (tabIndex: string) => {
    if (!this.state.customer) {
      return;
    }

    this.setTabInQuery(tabIndex);

    if (tabIndex !== this.state.activeTab) {
      this.setState({ activeTab: tabIndex });
    }
  };

  navigateBack = () => {
    const listUrl = this.props.match.url.replace(/\/[^/]+$/, '');
    this.props.history.push(listUrl);
  };

  render() {
    const customer = this.state.customer;

    return (
      <Row gutter={12}>
        <Col xs={24}>
          <TitledContainer
            title={
              customer
                ? `Klant ${customer.customer_id} | ${customer.first_name} ${
                    customer.prefix || ''
                  } ${customer.last_name}`
                : 'Klant'
            }
          >
            <>
              <Row style={{ marginBottom: 10 }}>
                <Col xs={24}>
                  <Button htmlType='button' onClick={this.navigateBack}>
                    <Icon type='arrow-left' /> Terug naar klanten
                  </Button>
                </Col>
              </Row>

              {!customer && this.props.customer.myCustomersLoading && <Spin />}

              {!customer && !this.props.customer.myCustomersLoading && (
                <em style={{ color: '#aaa' }}>Klant niet gevonden</em>
              )}

              {customer && (
                <Tabs activeKey={this.state.activeTab} animated={false} onChange={this.onTabChange}>
                  <TabPane tab='Profiel' key='1'>
                    <ProfileTab customerId={customer.id} />
                  </TabPane>
                  <TabPane tab='Bestellingen' key='2'>
                    {customer.postal_code && <OrderTab customerId={customer.id} />}
                  </TabPane>
                </Tabs>
              )}
            </>
          </TitledContainer>
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = ({ customer, auth }: AppState) => ({
  customer,
  auth
});

export default connect(mapStateToProps)(Form.create()(CustomerById));
